// Copyright (c) Bentley Systems
import { Checkbox, SelectOption } from "@itwin/itwinui-react";
import { SvgVisibilityHide, SvgVisibilityShow } from "@itwin/itwinui-icons-react";
import { SystemProperty } from "./Models/system-property";
import LabeledInputComponent from "../../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import CustomLabeledSelect from "../../CustomInputComponents/CustomLabeledSelect";
import { HTMLInputTypeAttribute, useState } from "react";
import {
  getExclusivePartner,
  getPropertyLabel,
  isBooleanPropertyChecked,
  isPropertyRequired,
  isPropertyVisible,
} from "./token-auth-rules";

type SystemPropertiesProps = {
  systemProperties: SystemProperty[] | undefined;
  onSystemPropertiesChange: (systemProperties: SystemProperty[]) => void;
};

function populateEnumOptions(data: string[]) {
  const enumOptions: SelectOption<string>[] = [];
  for (let i = 0; i < data.length; i++) {
    enumOptions[i] = {
      label: data[i],
      value: data[i],
    };
  }
  return enumOptions;
}

const SystemProperties = (props: SystemPropertiesProps) => {
  const [visibleFieldIndexes, setVisibleFieldIndexes] = useState<Set<number>>(
    new Set(),
  );
  const systemProperties = props.systemProperties ?? [];

  function updatePropertyValues(values: Map<string, string>) {
    props.onSystemPropertiesChange(
      systemProperties.map((p) => {
        const value = values.get(p.name);
        return value === undefined ? p : { ...p, defaultValue: value };
      }),
    );
  }

  function setSysPropertyChange(
    sysProperty: SystemProperty,
    currentValue: string,
  ) {
    updatePropertyValues(new Map([[sysProperty.name, currentValue]]));
  }

  function setBooleanPropertyChange(
    sysProperty: SystemProperty,
    checked: boolean,
  ) {
    const values = new Map([[sysProperty.name, String(checked)]]);
    const partnerName = getExclusivePartner(sysProperty.name);
    if (checked && partnerName !== undefined) {
      values.set(partnerName, "false");
    }
    updatePropertyValues(values);
  }

  function toggleFieldVisibility(fieldIndex: number) {
    setVisibleFieldIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(fieldIndex)) {
        next.delete(fieldIndex);
      } else {
        next.add(fieldIndex);
      }
      return next;
    });
  }

  function getInputType(
    isSensitive: boolean | undefined,
    isVisible: boolean,
  ): HTMLInputTypeAttribute | undefined {
    return isSensitive && !isVisible ? "password" : "text";
  }

  function renderSwitchFieldType(
    sysProperty: SystemProperty,
    fieldIndex: number,
  ) {
    const componentKey = `field_${fieldIndex}`;
    const isVisible = visibleFieldIndexes.has(fieldIndex);
    const label = getPropertyLabel(sysProperty.name);
    const labeledInput = (
      <LabeledInputComponent
        type={getInputType(sysProperty.isSensitive, isVisible)}
        displayStyle="default"
        label={label}
        required={isPropertyRequired(systemProperties, sysProperty.name)}
        placeholder="Enter text here..."
        validationType={EfFieldValidationTypeEnum.Default}
        disabled={false}
        key={componentKey}
        defaultValue={sysProperty.defaultValue}
        svgIcon={
          sysProperty.isSensitive ? (
            isVisible ? (
              <SvgVisibilityHide />
            ) : (
              <SvgVisibilityShow />
            )
          ) : undefined
        }
        iconProps={
          sysProperty.isSensitive
            ? {
                onClick: () => toggleFieldVisibility(fieldIndex),
                style: { cursor: "pointer" },
              }
            : undefined
        }
        onChange={(currentValue) => {
          setSysPropertyChange(sysProperty, currentValue.target.value);
        }}
        onBlur={(e) => {
          const trimmedValue = e.target.value.trim();
          if (trimmedValue !== e.target.value) {
            e.target.value = trimmedValue;
            setSysPropertyChange(sysProperty, trimmedValue);
          }
        }}
      />
    );
    switch (sysProperty.dataType.toLowerCase()) {
      case "boolean":
        return (
          <Checkbox
            style={{ marginTop: "0.2rem" }}
            label={label}
            key={componentKey}
            onChange={(e) => {
              setBooleanPropertyChange(sysProperty, e.target.checked);
            }}
            checked={isBooleanPropertyChecked(sysProperty)}
          />
        );
      case "string[]":
        return (
          <div key={componentKey}>
            <CustomLabeledSelect
              label={label}
              options={populateEnumOptions(sysProperty.enumValues) || []}
              onChange={(currentValue: string) => {
                setSysPropertyChange(sysProperty, currentValue);
              }}
              value={sysProperty.defaultValue}
            />
          </div>
        );

      case "int32":
        return (
          <LabeledInputComponent
            displayStyle="default"
            label={label}
            validationType={EfFieldValidationTypeEnum.IntType}
            placeholder="Enter text here..."
            disabled={false}
            key={componentKey}
            defaultValue={sysProperty.defaultValue}
            onChange={(currentValue) => {
              setSysPropertyChange(sysProperty, currentValue.target.value);
            }}
          />
        );
      default:
        return labeledInput;
    }
  }

  return (
    <div>
      {systemProperties.map((sysProperty, index) => {
        if (!isPropertyVisible(systemProperties, sysProperty.name)) {
          return null;
        }
        return (
          <div key={`prop__${index}`} className="becs-t-margin">
            {renderSwitchFieldType(sysProperty, index)}
          </div>
        );
      })}
    </div>
  );
};

export default SystemProperties;
